import React, { useCallback, useState } from 'react';
import cl from 'classnames';

import { TeamUUID } from '../../../../../../teams/teamsTypes';
import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { IsDisabled } from '../../../../../../../types';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import { Translate } from '../../../../../../../helpers/Translate';
import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import { ProductsPermissions } from '../../../../../../products/productsConstants';

import { words } from '../../../../../../../locales/keys';

interface ThreeDStockProductsFiltersFavoritesFilterProps {
  teamUuid?: TeamUUID;
  productsFilters: FetchProductsFilters;
  disabled?: IsDisabled;
  changeProductsFilters: ChangeProductsFiltersFunc;
  isCompact?: boolean;
}

const favoriteFieldName = FetchProductsFilterFields.FAVORITE;
const teamIdFavoriteFieldName = FetchProductsFilterFields.TEAM_ID_FAVORITE;
const currentTeamFavoriteFieldName =
  FetchProductsFilterFields.CURRENT_TEAM_FAVORITE;

function ThreeDStockProductsFiltersFavoritesFilter({
  teamUuid,
  productsFilters,
  disabled,
  changeProductsFilters,
  isCompact
}: ThreeDStockProductsFiltersFavoritesFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const isFavorites = productsFilters?.[favoriteFieldName];
  const handleFavoriteClick = useCallback(() => {
    if (isFavorites) {
      changeProductsFilters({}, [favoriteFieldName]);
    } else {
      changeProductsFilters({ [favoriteFieldName]: true }, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }
  }, [isFavorites, changeProductsFilters]);

  const isTeamFavorites = productsFilters?.[teamIdFavoriteFieldName];
  const isCurrentTeamFavorites =
    productsFilters?.[currentTeamFavoriteFieldName];

  const handleTeamFavoriteClick = useCallback(() => {
    if (isTeamFavorites || isCurrentTeamFavorites) {
      return changeProductsFilters({}, [
        teamIdFavoriteFieldName,
        currentTeamFavoriteFieldName
      ]);
    }

    const filterName = teamUuid
      ? teamIdFavoriteFieldName
      : currentTeamFavoriteFieldName;

    const filterValue = teamUuid ? teamUuid : true;

    changeProductsFilters({ [filterName]: filterValue }, [favoriteFieldName]);
  }, [
    isTeamFavorites,
    isCurrentTeamFavorites,
    changeProductsFilters,
    teamUuid
  ]);

  return (
    <CheckPermissions
      actions={[
        ProductsPermissions.READ_PRODUCTS_FAVORITE_CHECKBOX_FILTER,
        ProductsPermissions.READ_PRODUCTS_COMPANY_FAVORITE_CHECKBOX_FILTER
      ]}
    >
      <div className={cl(isCompact ? 'p-2' : 'p-4')}>
        <div className={cl('flex flex-col', isCompact ? 'gap-1.5' : 'gap-3')}>
          <ButtonHelper
            className="flex items-center gap-2 rounded-sm"
            dataGa="favorites"
            onClick={toggleIsOpen}
          >
            <span
              className={cl(
                'font-semibold text-left flex-1',
                isCompact ? 'text-xs' : 'text-sm'
              )}
            >
              <Translate id={words.favorites} />
            </span>

            <Icon
              icon={
                isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP
              }
              className={cl('text-gray-500', isCompact ? 'h-4 w-4' : 'h-5 w-5')}
            />
          </ButtonHelper>

          {isOpen && (
            <div
              className={cl('flex flex-wrap', isCompact ? 'gap-1.5' : 'gap-2')}
            >
              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureIconButtonHelper
                  className={cl(
                    'border rounded flex items-center box-content',
                    isCompact ? 'text-xs h-6' : 'text-sm h-8',
                    isFavorites
                      ? 'border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  )}
                  icon={IconsEnum.HEART_SOLID}
                  iconClassName={cl(
                    'ml-2 text-pink-500',
                    isCompact ? '-mr-1.5 h-3.5 w-3.5' : '-mr-2 h-4 w-4'
                  )}
                  i18nText={words.favorites}
                  i18nTextClassName={cl(
                    'whitespace-nowrap',
                    isCompact ? 'px-2' : 'px-3'
                  )}
                  onClick={handleFavoriteClick}
                  dataGa="favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>

              <CheckPermissions
                action={
                  ProductsPermissions.READ_PRODUCTS_MY_COMPANY_FAVORITE_CHECKBOX_FILTER
                }
              >
                <PureButtonHelper
                  className={cl(
                    'border rounded flex items-center box-content',
                    isCompact ? 'px-2 text-xs h-6' : 'px-3 text-sm h-8',
                    isTeamFavorites || isCurrentTeamFavorites
                      ? 'border-blue-200 dark:border-transparent bg-blue-100 hover:bg-blue-50 text-black'
                      : 'border-gray-300 dark:border-gray-700 hover:bg-gray-500/10 text-gray-700 dark:text-gray-300'
                  )}
                  i18nText={words.companyFavorites}
                  i18nTextClassName="inline"
                  onClick={handleTeamFavoriteClick}
                  dataGa="team-favorites-filter"
                  disabled={disabled}
                />
              </CheckPermissions>
            </div>
          )}
        </div>
      </div>
    </CheckPermissions>
  );
}

export default ThreeDStockProductsFiltersFavoritesFilter;
